import { Control } from "Controls/Control";
import { TextBlockService, CommandService } from "Service";
import { IFileUploadParameter } from "Entities";
import { App } from "Core";
import { ControlDefinition, Environment } from "Service/Client";

/**
 * Control for file upload.
 * The Textblocks used in this control are in the TextblockCategory 'fileupload'.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of an CustomControl with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class FileUpload extends Control {
    private _kendoElement: kendo.ui.Upload;

    private commandService: CommandService;

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-fileupload";

    /**
     * @hidden
     */
    protected bindingProperty: string = null;

    /**
     * @hidden
     */
    protected defaultEditorElement: string = `<input type="file" name="files"/>`;

    /**
     * Create a new FileUploadControl.
     * @param controlDefinition
     * @param textBlockService
     */
    constructor(
        controlDefinition: ControlDefinition,
        textBlockCategory: string,
        textBlockService: TextBlockService,
        environment: Environment,
        commandService: CommandService
    ) {
        super(controlDefinition, textBlockCategory, textBlockService, environment);
        this.commandService = commandService;
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     */
    protected setElementInternal(element: JQuery): void {
        this._kendoElement = new kendo.ui.Upload(this.jQueryElement[0], {
            async: {
                saveUrl: "dmsapi/fileupload/upload",
                removeUrl: "dmsapi/fileupload/remove",
            },
            multiple: false,
            upload: (e) => this.onUpload(e),
            remove: (e) => this.onRemove(e),
            validation: {
                allowedExtensions: this.environment.FileUploadExtensionWhitelist || [],
            },
            // localization: {
            //     select: "&nbsp;",
            // },
        });

        // the fileuploadcontrol has many textblocks but the control itself is rarely used.
        // therefore the textblocks are loaded on initialization.
        this.textBlockService.load(this.environment.LanguageCode, "fileupload").then((tb) => {
            let textblocks = tb["fileupload"];
            let loc = (<any>this._kendoElement).localization;

            loc.select = this.textBlockService.getChecked(textblocks, "Select", "fileupload");
            loc.cancel = this.textBlockService.getChecked(textblocks, "Cancel", "fileupload");
            loc.remove = this.textBlockService.getChecked(textblocks, "Remove", "fileupload");
            loc.dropFilesHere = this.textBlockService.getChecked(textblocks, "DropFilesHere", "fileupload");
            loc.headerStatusUploaded = this.textBlockService.getChecked(textblocks, "StatusUploaded", "fileupload");
            loc.headerStatusUploading = this.textBlockService.getChecked(textblocks, "StatusUploading", "fileupload");
            loc.statusFailed = this.textBlockService.getChecked(textblocks, "StatusFailed", "fileupload");
            loc.statusUploaded = this.textBlockService.getChecked(textblocks, "StatusUploaded", "fileupload");
            loc.statusUploading = this.textBlockService.getChecked(textblocks, "StatusUploading", "fileupload");
            loc.invalidFileExtension = this.textBlockService.getChecked(textblocks, "InvalidFileExtension", "fileupload");
            // since the localization of the already rendered element is not updated when setting localization,
            // the textblocks of the button / dropzone is set manually.
            this._kendoElement.wrapper.find(".k-button > span").html(this.textBlockService.getChecked(textblocks, "Select", "fileupload"));
            this._kendoElement.wrapper
                .find(".k-dropzone > em")
                .html(this.textBlockService.getChecked(textblocks, "DropFilesHere", "fileupload"));
        });
    }

    private onRemove(e: kendo.ui.UploadRemoveEvent): void {
        let app = <App>(<any>window).app;
        e.headers = { Authorization: "Bearer " + app.auth.user.access_token };
        this.setRequestParameters(e);
    }

    private onUpload(e: kendo.ui.UploadUploadEvent): void {
        let app = <App>(<any>window).app;
        let xhr = e.XMLHttpRequest;
        xhr.addEventListener("readystatechange", function (e: any) {
            if (xhr.readyState == 1 /* OPENED */) {
                xhr.setRequestHeader("Authorization", "Bearer " + app.auth.user.access_token);
            }
        });
        this.setRequestParameters(e);
    }

    private setRequestParameters(e: kendo.ui.UploadEvent | kendo.ui.UploadRemoveEvent) {
        let param = <IFileUploadParameter>this.commandService.getContextParameters();
        param.ControlID = this.controlDefinition.ControlID;
        (<any>e).data = param;
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     */
    public getOuterElement(): JQuery {
        return this._kendoElement != null ? this._kendoElement.wrapper : this.jQueryElement;
    }

    /**
     * Gets the display template for formatting in grid row.
     * @hidden
     */
    protected getDisplayTemplate(bindingField?: string): string {
        return ``;
    }

    /**
     * Get the kendo upload control. Click {@link http://docs.telerik.com/kendo-ui/api/javascript/ui/upload|here} for the api definition.
     * @since 4.0.0
     */
    public getKendoElement(): kendo.ui.Upload {
        return this._kendoElement;
    }

    /**
     * Not implemented for this control type.
     * @param value
     * @hidden
     */
    public setValue(value: string): void {}

    /**
     * Gets the file name of the selected file or null if no file was selected.
     * @since 4.0.0
     */
    public getValue(): string {
        let files = this._kendoElement.getFiles();
        return files.length > 0 ? files[0].name : null;
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement != null) {
            this._kendoElement.destroy();
        }
    }
}
